import {
  type SetStateAction,
  Ref,
  useCallback,
  useImperativeHandle,
} from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { ItemNoAndTypeWithQuantity } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  AttachArticlesWithQuantityStepper,
  ArticleWithQuantity,
} from "@coworker/apprestructured/src/task-management/components/AttachArticles/AttachArticlesWithQuantityStepper";

export type AttachArticlesWithQuantityStepperCreatorFragmentConfigProps = {
  field: string;
  maxArticles: number;
};

type AttachArticlesWithQuantityStepperCreatorFragmentProps = {
  configProps: AttachArticlesWithQuantityStepperCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function AttachArticlesWithQuantityStepperCreatorFragment({
  configProps,
  commonProps,
  ref,
}: AttachArticlesWithQuantityStepperCreatorFragmentProps) {
  const { field, maxArticles } = configProps;
  const { blockInput, task, onTaskChange, taskType } = commonProps;

  // Level 1: Schema validation — does the data have the right shape?
  const itemNosAndTypeWithQuantity = validateInitialDetailsFieldValue<
    ItemNoAndTypeWithQuantity[]
  >(getByPath(task, field), field, taskType);

  // Level 2: Business validation — do the items actually exist?
  const { items, isFetchingItems } = useAllAttachedItems(
    isLoading(itemNosAndTypeWithQuantity)
      ? LOADING
      : (itemNosAndTypeWithQuantity ?? [])
  );

  const articles: ArticleWithQuantity[] = Array.isArray(
    itemNosAndTypeWithQuantity
  )
    ? itemNosAndTypeWithQuantity
        .map((articleWithQuantity) => {
          const item = items.find((i) => i.no === articleWithQuantity.no);
          if (!item) return null;
          return { item, quantity: articleWithQuantity.quantity };
        })
        .filter((a): a is ArticleWithQuantity => a !== null)
    : [];

  const handleSetArticles = useCallback(
    (updater: SetStateAction<ArticleWithQuantity[]>) => {
      const next = typeof updater === "function" ? updater(articles) : updater;
      const articleWithQuantity: ItemNoAndTypeWithQuantity[] = next.map(
        ({ item, quantity }) => ({
          no: item.no,
          type: item.type,
          quantity,
        })
      );
      onTaskChange(setByPath(field, articleWithQuantity));
    },
    [articles, field, onTaskChange]
  );

  const valid =
    !isLoading(itemNosAndTypeWithQuantity) &&
    Array.isArray(itemNosAndTypeWithQuantity) &&
    !isFetchingItems &&
    articles.length > 0 &&
    articles.length === itemNosAndTypeWithQuantity.length;

  useImperativeHandle(ref, () => ({ valid }));

  if (isLoading(itemNosAndTypeWithQuantity) || isFetchingItems) {
    return <LoadingCreatorFragment />;
  }

  return (
    <AttachArticlesWithQuantityStepper
      articles={articles}
      setArticles={handleSetArticles}
      maxArticles={maxArticles}
      isActive={!blockInput}
    />
  );
}

export default AttachArticlesWithQuantityStepperCreatorFragment;
